/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useEffect, useState } from "react";
import {
	DatePicker,
  Button,
  Space,
  Tag,
  Tooltip
} from "antd";
import { audioMessage as message } from '../../Components/Common/Notifications/audioMessage';
import {DeleteOutlined} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import RadarChart from '../Charts/RadarChart';
import { mapRadarData } from '../../mappers/model/RadarChart';
import BarChart from '../Charts/BarChart';
import { mapBarChartData } from '../../mappers/model/BarChartMapper';
import {mapDataForMultiCompareBarChart} from "../../mappers/model/GroupedBarChartMapper";
import {
  getMultiRangeComparisonData,
  setMultiRangeCompareData,
  setMultiRangeCompareFilterDateRange,
  setMultiRangeCompareFilterMetrics,
  setMultiRangeCompareFilterPlayerSelection,
  setMultiRangeCompareDataError
} from "../../store/actions/compare.actions";
import moment from "moment";
import { OptionGroupSelect } from './Components_1v1/OptionGroupSelect'
import { PlayerInfoCard } from './Components_1v1/PlayerInfoCard'
import { AggregateRow } from './Components_Multi/AggregateRow'
import { getPercentageValues } from "../../Utils/chart_utils"
import { LuCalendarPlus } from "react-icons/lu";

const { RangePicker } = DatePicker;

export const AGGREGATE_OPTIONS = {
  AVG: 'avg',
  SUM: 'sum',
  BEST: 'best'
}

export const ComparisonMultiRange = ({ activeTab })  => {
  const dispatch = useDispatch();

  const {
    games,
    positions,
    playerGroups: groups,
    allPlayers,
		all_metric_types,
    info: { team_colors: teamColors },
	} = useSelector(({ team }) => team);

  const {
    filter_multi_range: {
      dateRangeMap,
      selectedMetrics,
      playerCardSelections
    },
    data_multi_range,
    error
  } = useSelector(({ compare }) => compare);

  const MAX_SELECTION_THRESHOLD = 10;
  const [pickers, setPickers] = useState([{}, {}]); // Initialize with two picker
  const [isAddDrillDisabled, setIsAddDrillDisabled] = useState(true); // Initialize with one picker
  const [showRemoveDateRangeButton, setShowRemoveDateRangeButton] = useState(false);
  const maxPickers = 6; // Set the cap to 6
  const [loading, setLoading] = useState(false);
  const [radarChartData, setRadarChartData] = useState(null);
  const [playerPosGroupOptions, setPlayerPosGroupOptions] = useState([]);
  const [metricOptions, setMetricOptions] = useState([]);
  const [aggregateDataByMetric, setAggregateDataByMetric] = useState(null);
  const [barChartDataByMetric, setBarChartDataByMetric] = useState(null);
  const [cumulativeChartData, setCumulativeChartData] = useState(null);
  const [metricAnnotationMap, setMetricAnnotationMap] = useState({});
  const [dateRangeIdToLabelMap, setDateRangeIdToLabelMap] = useState({});
  const [dateRangeIds, setDateRangeIds] = useState([]);
  const [selectedRangeShortcut, setSelectedRangeShortcut] = useState(null);

  const date_range_shortcuts = [
    {
      key: 1,
      label: "Last 4 Weeks",
      value: "4 weeks"
    },
    {
      key: 2,
      label: "Last 5 Weeks",
      value: "5 weeks"
    },
    {
      key: 3,
      label: "Last 6 Weeks",
      value: "6 weeks"
    },
    {
      key: 4,
      label: "Last 3 Months",
      value: "3 months"
    },
    {
      key: 5,
      label: "Last 5 Games",
      value: "5 games"
    }
  ]

  const resetAllValues = () => {
    dispatch(setMultiRangeCompareFilterDateRange(0, null, null));
    dispatch(setMultiRangeCompareFilterDateRange(1, null, null));
    dispatch(setMultiRangeCompareFilterDateRange(2, null, null));
    dispatch(setMultiRangeCompareFilterDateRange(3, null, null));
    dispatch(setMultiRangeCompareFilterDateRange(4, null, null));
    dispatch(setMultiRangeCompareFilterMetrics([]));
    dispatch(setMultiRangeCompareFilterPlayerSelection([]));
    dispatch(setMultiRangeCompareDataError(null));
    dispatch(setMultiRangeCompareData({}));
    setLoading(false);
    setRadarChartData(null);
    setAggregateDataByMetric(null);
    setBarChartDataByMetric(null);
    setCumulativeChartData(null);
    setMetricAnnotationMap({});
    setDateRangeIdToLabelMap({});
    setPickers([{}, {}]);
    setDateRangeIds([]);
    setShowRemoveDateRangeButton(false);
  }

  useEffect(() => {
    resetAllValues();
  }, [])

  useEffect(() => {
    resetAllValues();
  }, [activeTab])

  useEffect(() => {
    if (error) {
      setLoading(false);
      message.error(error);
      dispatch(setMultiRangeCompareDataError(null));
    }
  }, [error])

  useEffect(() => {
    if (data_multi_range && Object.keys(data_multi_range).length > 0) {
      console.log("data_multi_range from api", data_multi_range)
      setLoading(false);
      message.success("Data retrieved successfully!");
      const rangeIds = Object.keys(data_multi_range?.aggregates)
      setDateRangeIds(rangeIds)
      const metricNamesAndIds = data_multi_range?.radar_data?.x_axis || []
      const dateMap = {}
      rangeIds.forEach( (rangeId, index) => {
        const dates = dateRangeMap[index]
        if(dates && dates?.length === 2) {
          dateMap[rangeId] = `${dates[0].format("YYYY-MM-DD")} to ${dates[1].format("YYYY-MM-DD")}`
        }
      })
      setDateRangeIdToLabelMap(dateMap);
      extractRadarChartData(data_multi_range?.radar_data, rangeIds, dateMap);
      extractBarChartData(data_multi_range?.bar_data, metricNamesAndIds, rangeIds, dateMap);
      const aggregateData = extractAggregateData(data_multi_range?.aggregates, metricNamesAndIds, rangeIds);
      setAggregateDataByMetric(aggregateData);
      extractCumulativeChartData(aggregateData, rangeIds, dateMap);

      const annotationMap = {}
      for (let i = 0; i < metricNamesAndIds.length; i++) {
        annotationMap[metricNamesAndIds[i].identifier] = "avg";
      }
      setMetricAnnotationMap(annotationMap);
    }
  }, [data_multi_range])


  useEffect(() => {
    if (data_multi_range && Object.keys(data_multi_range).length > 0 && metricAnnotationMap) {
      const metricNamesAndIds = data_multi_range?.radar_data?.x_axis || []
      const rangeIds = Object.keys(data_multi_range?.aggregates)
      extractRadarChartData(data_multi_range?.radar_data, rangeIds, dateRangeIdToLabelMap);
      extractBarChartData(data_multi_range?.bar_data, metricNamesAndIds, rangeIds, dateRangeIdToLabelMap);
    }
  }, [metricAnnotationMap])

  const updateIsAddDrillDisabled = (dateRangeMap, pickers_length) => {
    let pickerSelectedRange = [];
    for (const [index, range] of Object.entries(dateRangeMap)) {
      if (parseInt(index) + 1 <= pickers_length)
        pickerSelectedRange.push(range)
    }
    
    const addDrillDisabled = pickerSelectedRange.some((range) => range === null) || pickers_length >= maxPickers
    setIsAddDrillDisabled(addDrillDisabled)

    if(pickerSelectedRange.length > 2 && pickerSelectedRange.length <= 6) {
      setShowRemoveDateRangeButton(true);
    } else {
      setShowRemoveDateRangeButton(false);
    }
  }

  useEffect(() => {
    updateIsAddDrillDisabled(dateRangeMap, pickers.length);
  }, [dateRangeMap])

  const getOptionGroup = (optionGroupName, JSONOptions, metric_type) => {
    let metricObj = {}, metricOps = [];
    metricObj["label"] = <span>{optionGroupName}</span>;
    metricObj["title"] = optionGroupName;
    
    for (let i = 0; i < JSONOptions.length; i++) {
      const metric = JSONOptions[i];
      if (["gps", "forceplate", "testing", "wellness", "nutrition"].includes(metric_type)) {
        metricOps.push({
          label: <span>{metric["name"]}</span>,
          value: `${metric_type}-${metric["metric_id"]}`,
          key: metric["metric_id"]
        });
      }
      else if (metric_type === "player") {
        metricOps.push({
          label: <span>{`${metric["first_name"]} ${metric["last_name"]}`}</span>,
          value: `${metric_type}-${metric["player_id"]}`,
          key: metric["player_id"]
        });
      }
      else if (metric_type === "pos") {
        metricOps.push({
          label: <span>{metric}</span>,
          value: `${metric_type}-${metric}`,
          key: metric
        });
      }
      else if (metric_type === "group") {
        metricOps.push({
          label: <span>{metric["name"]}</span>,
          value: `${metric_type}-${metric["player_group_id"]}`,
          key: metric["player_group_id"]
       });
      }
    }

    if (metric_type === "gps") {
      metricObj["options"] = metricOps;
    } else {
      metricObj["options"] = metricOps.sort((a, b) => {
        const valueA = a?.label?.props?.children || "";
        const valueB = b?.label?.props?.children || "";
        return valueA.localeCompare(valueB);
      });
    }
    return metricObj;
  }

  useEffect(() => {
    let groupOptions = [];
    for (const [metric_type, metrics] of Object.entries(all_metric_types)) {
      const metricsFiltered = metrics?.filter((e) => e.visible === true || e.visible === undefined)
      if (metricsFiltered.length > 0) {
        let groupLabel;
        if (metric_type === "gps") groupLabel = "GPS";
        else groupLabel = metric_type.charAt(0).toUpperCase() + metric_type.slice(1).toLowerCase(); //Capitalize
        groupOptions.push(getOptionGroup(groupLabel, metricsFiltered, metric_type));
      }
    }

    setMetricOptions(groupOptions)
  }, [all_metric_types])

  useEffect(() => {
    let groupOptions = [], metricObj = {};
    metricObj["label"] = <span>Team</span>;
    metricObj["title"] = "Team";
    metricObj["options"] = [{
      label: <span>Full Team</span>,
      value: "full-team",
      key: "full-team"
    }];
    
    groupOptions.push(metricObj);

    if (allPlayers.length > 0) {
      groupOptions.push(getOptionGroup("Players", allPlayers, "player"));
    }

    if (positions.length > 0) {
      groupOptions.push(getOptionGroup("Positions", positions, "pos"));
    }

    if (groups.length > 0) {
      groupOptions.push(getOptionGroup("Groups", groups, "group"));
    }

    setPlayerPosGroupOptions(groupOptions);
    dispatch(setMultiRangeCompareFilterPlayerSelection(['full-team']));
  }, [positions, groups, allPlayers])

  const handleDateRangeChange = (index, datesMoments, calledFromShortcut, shortcut) => {
    if (!calledFromShortcut) {
      setSelectedRangeShortcut(null);
    }

    if (datesMoments === null) {
      dispatch(setMultiRangeCompareFilterDateRange(index, null, null));
      return
    }

    const startDate = datesMoments[0], endDate = datesMoments[1];
    const incoming_day_diff = endDate.diff(startDate, 'days');
    let existing_max_day_diff = 0, old_day_diff;
    let thresholdDateRange = [null, null]
    for (const [rangeIndex, range] of Object.entries(dateRangeMap)) {
      if (range !== null) {
        const rangeStartDate = range[0];
        const rangeEndDate = range[1];
        old_day_diff = existing_max_day_diff;
        existing_max_day_diff = Math.max(existing_max_day_diff, rangeEndDate.diff(rangeStartDate, 'days'));
        if (existing_max_day_diff > 0 && old_day_diff !== existing_max_day_diff) {
          thresholdDateRange[0] = rangeStartDate;
          thresholdDateRange[1] = rangeEndDate;
        }
      }

      if (existing_max_day_diff > 0 && incoming_day_diff > existing_max_day_diff && shortcut === null) {
        message.error({
          duration: 7,
          content: (<>Num of days must be less than or equal to the maximum selected date range: <br/>
          [{thresholdDateRange[0].format("YYYY-MM-DD")} to {thresholdDateRange[1].format("YYYY-MM-DD")}] ({existing_max_day_diff} days)
          </>)
        })
        return
      }
    }

    dispatch(setMultiRangeCompareFilterDateRange(index, startDate, endDate));
  }

  const handleOptionGroupChange = (value) => {
    if (value.length > MAX_SELECTION_THRESHOLD) {
      message.warning(`You can't select more than ${MAX_SELECTION_THRESHOLD} players, positions, groups.`)
    }
    else {
      // console.log(`selected left ${value}`);
      dispatch(setMultiRangeCompareFilterPlayerSelection(value));
    }
  }

  const handleMetricOptionGroupChange = (value) => {
    if (value.length > MAX_SELECTION_THRESHOLD) {
      message.warning(`You can't select more than ${MAX_SELECTION_THRESHOLD} metrics.`)
    }
    else {
      // console.log(`selected metric ${value}`);
      dispatch(setMultiRangeCompareFilterMetrics(value));
    }
  }

  const convertStringToMoment = (dateString) => {
    if (!dateString) {
      return null;
    }
    return moment(dateString);
  }

  const addPicker = () => {
    if (pickers.length < maxPickers) {
      const newPickers = [...pickers, {}];
      setPickers(newPickers);
      setSelectedRangeShortcut(null);
      updateIsAddDrillDisabled(dateRangeMap, newPickers.length);
    }
  };

  const removeLastDateRange = () => {
    const indexToRemove = pickers.length - 1;
    const newPickers = pickers.slice(0, -1);
    handleDateRangeChange(indexToRemove, null, false, null)
    setPickers(newPickers);
    updateIsAddDrillDisabled(dateRangeMap, newPickers.length);
  }

  const extractAggregateData = (data, metricNamesAndIds, rangeIds) => {
    if (!data) return;

    const dataForSelectedAggregateOption = metricNamesAndIds.map( (metric) => {
      let aggregateObject = {
        ...metric,
        colors: teamColors.all
      }

      rangeIds.forEach( (rangeId) => {
        const metricDataForRange = data[rangeId].find((metricData) => metricData.key === metric.identifier)
        aggregateObject = {
          ...aggregateObject,
          [rangeId]: metricDataForRange
        }
      })

      return aggregateObject;
    })

    return dataForSelectedAggregateOption;
  }

  const extractRadarChartData = (data, rangeIds, dateRangeIdToLabelMap) => {
    if (!data) return;

    // TODO: get team best
    const maxValuesPerCategory = [];
    const dateRangeValueMap = {}
    rangeIds.forEach( (rangeId) => {
      dateRangeValueMap[rangeId] = []
    })
  
    if ((metricAnnotationMap !== undefined || metricAnnotationMap !== null) && data) {
      const x_axis = data.x_axis;
      
      if (x_axis) {
        for (let i = 0; i < x_axis.length; i++) {
          let annotationToUse = "avg";
          const metric_identifier = x_axis[i].identifier;
          const metricAnnot = metricAnnotationMap[metric_identifier];
          if (metricAnnot !== undefined) {
            annotationToUse = metricAnnot;
          }

          rangeIds.forEach( (rangeId) => {
            dateRangeValueMap[rangeId].push(data[rangeId][annotationToUse][i])
          })
        }
      }
    }

    const x_axis = data.x_axis;
    for (let i = 0; i < x_axis.length; i++) {
      const identifier = x_axis[i].identifier;
      
      if (identifier !== null || identifier !== undefined) {
        const metric_annotation = metricAnnotationMap[identifier] || "avg";
        
        if (metric_annotation !== null || metric_annotation !== undefined) {
          const maxVal = x_axis[i][`${metric_annotation}-max`]
          maxValuesPerCategory.push(maxVal)
        }
      }
    }

    const allDateRangeSeries = rangeIds.map( (rangeId, index) => {
      return {
        data: addCustomInformationToRadarSeries(dateRangeValueMap[rangeId], maxValuesPerCategory),
        name: dateRangeIdToLabelMap[rangeId],
        pointPlacement: 'on',
        selected: true
      }
    })

    const xAxis = data.x_axis?.map( (item) => item.name) || []
    const tooltip = {
      shared: true,
      formatter: function () {
            const metricLine = `Metric: ${this.x} <br>`;
            let per_of_team_val = null, rawVal = null;
            const lines = this.points.map( point => {
              let annotationTooltip = "avg";
              if (data && data.x_axis) {
                const metric_key_id_obj =  data.x_axis.filter(axis_data => axis_data.name === point.key); 
                if (metric_key_id_obj) {
                  const metric_identifier = metric_key_id_obj[0].identifier;
                  const decimal_places = metric_key_id_obj[0]["decimal-places"];
                  const metricAnnot = metricAnnotationMap[metric_identifier];
                  if (metricAnnot !== undefined) {
                    annotationTooltip = metricAnnot;
                    rawVal = point?.point?.rawValue.toFixed(decimal_places) || 0;
                    let teamMaxVal = metric_key_id_obj[0][`${metricAnnot}-max`] || 0;
                    
                    if (teamMaxVal > 0) {
                      per_of_team_val = ((rawVal / teamMaxVal) * 100).toFixed(2);
                    }
                  }
                }
              }
              
              return `${point?.series?.name || ''} ${(annotationTooltip && annotationTooltip[0].toUpperCase() + annotationTooltip.slice(1)) || ""}: <strong>${rawVal || 0}</strong> (${per_of_team_val || 0}% of team best)`
            })
            return metricLine + lines.join('<br>');
      }
    }

    const paneSize = '70%';

    const radarData = mapRadarData(allDateRangeSeries, xAxis, teamColors.all, tooltip, paneSize)
    setRadarChartData(radarData);
  }

  const addCustomInformationToRadarSeries = (series = [], maxValuesPerCategory = []) => {
    const customizedSeries = series?.map( (rawValue, index) => {
      const maxValue = maxValuesPerCategory[index];
      if(!maxValue) {
        return {
          y: 0,
          rawValue
        }
      }

      const scaledPoint = Number(((rawValue / maxValue) * 100).toFixed(2));
      const customDataPoint = {
        y: scaledPoint,
        rawValue
      }

      return customDataPoint
    })
    return customizedSeries
  }

  const extractBarChartData = (bar_data, metricNamesAndIds, rangeIds, dateRangeIdToLabelMap) => {
    if (!bar_data) return;
    const barDataBySeries = {};
    const categories = bar_data.map( (data) => data.disp_label)
    metricNamesAndIds.forEach( (metric) => {
      const metric_identifier = metric.identifier;
      barDataBySeries[metric_identifier] = {
        categories,
        title: metric.name,
        series: [],
        decimal_places: metric['decimal-places'] || 1
      }

      let annotationToUse = "avg";
      if (metricAnnotationMap !== undefined || metricAnnotationMap !== null) {
        const metricAnnot = metricAnnotationMap[metric_identifier];
        if (metricAnnot !== undefined) {
          annotationToUse = metricAnnot;
        }
      }

      rangeIds.forEach( (rangeId, index) => {
        const seriesForDateRange = {
          name: dateRangeIdToLabelMap[rangeId],
          data: [],
          color: teamColors?.all[index]
        }

        bar_data.forEach( (datapoint) => {
          const point = datapoint[`${metric_identifier}-${rangeId}-${annotationToUse}`] || 0
          seriesForDateRange.data.push({y: point})
        })

        barDataBySeries[metric_identifier].series.push(seriesForDateRange)
      });
    })
    
    setBarChartDataByMetric(barDataBySeries);
  }

  const extractCumulativeChartData = (aggregateData, rangeIds, dateRangeIdToLabelMap) => {
    if (!aggregateData) return;
    const cumulativeBarData = [];
    const categories = aggregateData.map( (data) => data.name)

    Object.values(AGGREGATE_OPTIONS).forEach( (aggregateOption) => {
      const chartDataForAggregateOption = {
        categories,
        title:  (aggregateOption && aggregateOption[0].toUpperCase() + aggregateOption.slice(1)) || "",
        series: []
      }

      const maxRawValuesForMetric = {};
      // Collect raw values first so we can calculate the scaled value later on
      rangeIds.forEach( (rangeId, index) => {
        aggregateData.forEach( (metric) => {
          if(!maxRawValuesForMetric[metric.name]) {
            maxRawValuesForMetric[metric.name] = []
          }
          const rawValue = metric?.[rangeId]?.values?.[aggregateOption] || 0;
          maxRawValuesForMetric[metric.name].push(rawValue)
        })
      })

      rangeIds.forEach( (rangeId, index) => {
        const seriesForDateRange = {
          name: dateRangeIdToLabelMap[rangeId],
          data: [],
          color: teamColors?.all[index]
        }

        aggregateData.forEach( (metric) => {
          const decimals = (metric["decimal-places"] !== undefined || metric["decimal-places"] !== null ? metric["decimal-places"] : 2);
          const rawValue = metric?.[rangeId]?.values?.[aggregateOption].toFixed(decimals) || 0
          const scaledValue = getPercentageValues(rawValue, decimals, maxRawValuesForMetric[metric.name])
          seriesForDateRange.data.push({y: scaledValue, rawValue})
        })

        chartDataForAggregateOption.series.push(seriesForDateRange)
      })

      cumulativeBarData.push(chartDataForAggregateOption)
    })
    
    setCumulativeChartData(cumulativeBarData);
  }

  const handleSubmitClick = () => {
    if (isAddDrillDisabled && pickers.length < 6) {
      message.error("Please select a valid date range");
      return
    }

    if (!(selectedMetrics && selectedMetrics.length > 0)) {
      message.error("Please select valid metrics values");
      return
    }

    if (!(playerCardSelections && playerCardSelections.length > 0)) {
      message.error("Please select valid values from drop-down menu for team/player/position/group");
      return
    }

    setLoading(true);
    dispatch(getMultiRangeComparisonData());
  }

  const handleMetricAnnotationChange = (metric, annotation) => {
    setMetricAnnotationMap({ ...metricAnnotationMap, [metric]: annotation });
  }

  const getLastNWeeks = (num_weeks) => {
    let weeks = [];
    let today = moment(); // Current date
    // Get Monday of the current week
    let currDay = today.clone();; 
    let currentMonday = today.clone().startOf('isoWeek'); // ISO weeks consider Monday as the start

    // Check if today is Sunday, otherwise find the last Sunday
    let lastSunday = today.day() === 0 ? today : today.day(0); // Get the last Sunday (today if it's Sunday)

    // Calculate the start and end dates for the last 3 weeks
    for (let i = num_weeks - 2; i >= 0 ; i--) {
      let endOfWeek = lastSunday.clone().subtract(i, 'weeks'); // Last Sunday
      let startOfWeek = endOfWeek.clone().subtract(6, 'days'); // Monday of that week

      weeks.push({
        start: startOfWeek,
        end: endOfWeek
      });
    }

    weeks.push({
      start: currentMonday, // Start of the current week (Monday)
      end: currDay // Current date as the end of the current week
    });

    return weeks;
  }

  const getLastNMonths = (num_months) => {
    let months = [];
    let today = moment(); // Current date
    let currentStartOfMonth = today.clone().startOf('month'); // Start of the current month

    // Loop to get start and end dates for the last N months
    for (let i = num_months - 1; i > 0; i--) {
      let startOfMonth = today.clone().subtract(i, 'months').startOf('month'); // Start of the month
      let endOfMonth = today.clone().subtract(i, 'months').endOf('month'); // End of the month

      months.push({
        start: startOfMonth,
        end: endOfMonth
      });
    }

    // Add current month
    months.push({
      start: currentStartOfMonth,
      end: today // Current date as end of the current month
    });

    return months;
  }

  const setDateRangeMapWithShortcuts = ({ date_ranges = [], dates = [], shortcut = null }) => {
    let pickersLen = 0;
    if (date_ranges.length > 0) {
      pickersLen = date_ranges.length;
      date_ranges.map((range, index) => {
        handleDateRangeChange(index, [range.start, range.end], true, shortcut);
      });
    }

    if (dates.length > 0) {
      if (dates.length < 2) {
        message.error(`Only ${dates.length} game${dates.length === 1 ? '' : 's'} found. Comparison is not possible.`)
        return
      }
      pickersLen = dates.length;
      dates.map((date, index) => {
        handleDateRangeChange(index, [date, date], true, shortcut);
      });
    }

    let newPickers = [...pickers];
    if (pickersLen > pickers.length) {
      pickersLen -= pickers.length;
      for (let i = 0; i < pickersLen ; i++) {
        newPickers.push({});
      }
    }
    else if (pickersLen < pickers.length) {
      const numPickersToRemove = pickers.length - pickersLen;
      for (let i = 0; i < numPickersToRemove ; i++) {
        newPickers.pop();
      }

      for (let i = 0; i < Object.keys(dateRangeMap).length ; i++) {
        if (i > pickersLen - 1){
          handleDateRangeChange(i, null, true, shortcut);
        }
      }
    }

    setPickers(newPickers);
    updateIsAddDrillDisabled(dateRangeMap, newPickers.length);
  }

  const setRangeShortcut = (shortcut) => {
    setSelectedRangeShortcut(shortcut);

    if (shortcut.includes("weeks")) {
      const num_weeks = parseInt(shortcut[0]);
      const date_ranges = getLastNWeeks(num_weeks);
      setDateRangeMapWithShortcuts({date_ranges, shortcut});
    } 
    else if (shortcut.includes("months")) {
      const num_months = parseInt(shortcut[0]);
      const date_ranges = getLastNMonths(num_months);
      setDateRangeMapWithShortcuts({date_ranges, shortcut});
    } 
    else if (shortcut.includes("games")) {
      const num_games = parseInt(shortcut[0]);
      const curr_day = moment();

      const last_game_dates_before_today = [];
      for (let i = 0; i < games.length ; i++) {
        const game_date = moment(games[i].date, 'YYYY-MM-DD');
        if (game_date.isBefore(curr_day)) {
          last_game_dates_before_today.push(game_date);
        }
      }
      last_game_dates_before_today.sort((a, b) => {
        return b.isBefore(a) ? -1 : b.isAfter(a) ? 1 : 0;
      });

      const last_n_game_dates = last_game_dates_before_today.slice(0, num_games);
      setDateRangeMapWithShortcuts({dates: last_n_game_dates, shortcut});
    }
  }


  return (
    <>
      <div className="compare-multi-range-container">
        <div className="data-filter-container">
          {/* 
            date selection left and right
          */}
          <div className="date-range-filters">
            <Space direction="horizontal" style={{ width: "100%" }}>
              {pickers.map((picker, index) => (
                <RangePicker
                  key={index}
                  className="range-picker"
                  onChange={e => handleDateRangeChange(index, e, false, null)}
                  value={!dateRangeMap[`${index}`] ? null : [convertStringToMoment(dateRangeMap[`${index}`][0]), convertStringToMoment(dateRangeMap[`${index}`][1])]}
                />
              ))}

              { showRemoveDateRangeButton ? (
                <Tooltip title="Delete date range">
                <Button
                  icon={<DeleteOutlined />} danger ghost
                  onClick={removeLastDateRange}
                />
                </Tooltip>
              ) : <></>}
              <Tooltip title="Add date range">
              <Button
                type="primary"
                icon={<LuCalendarPlus />}
                onClick={addPicker}
                disabled={isAddDrillDisabled}
              />
              </Tooltip>
            </Space>
          </div>
          <div className="date-range-shortcuts">
            {date_range_shortcuts.map((shortcut) => {
              return (
                <Tag
                  key={shortcut.key}
                  className="date-range-shortcuts-tag"
                  color={shortcut.value === selectedRangeShortcut ? '#1890ff' : 'default'} // Highlight selected tag
                  onClick={() => setRangeShortcut(shortcut.value)} // Set selected tag on click
                >
                  {shortcut.label}
                </Tag>
              )
            })}
          </div>
          {/* 
            metric selection left and right
          */}
           <div className="metric-multi-select-container"> 
            <OptionGroupSelect 
              selectedOptionVal={selectedMetrics}
              options={metricOptions}
              placeholder="Choose Metric"
              width={756}
              handleOptionGroupChange={handleMetricOptionGroupChange}
            />
          </div>
          {/* 
            player selection
          */}
          <div className="player-group-pos-container">
            <OptionGroupSelect 
              className="player-group-pos-left" 
              selectedOptionVal={playerCardSelections}
              options={playerPosGroupOptions}
              placeholder="Full Team"
              width={500}
              sideId='left' //just used to form unique key
              handleOptionGroupChange={handleOptionGroupChange}
            />
          </div>
          {/* 
            player info card
          */}
          <div className="player-info-card-container">
              {/* left */}
              <PlayerInfoCard 
                selectionList={playerCardSelections}
                sideId='left' //just used to add bg color
              />
          </div>
          {/* 
            submit button
          */}
          <div className="submit-button-container">
            <Button
              className="compare-1v1-submit-button"
              type="primary"
              onClick={handleSubmitClick}
              loading={loading}
            >
              Submit
            </Button>
          </div>

          {/* 
            show metrics in progress bar per date range.
            use segmented component on left to select from avg/best/sum
          */}
          {aggregateDataByMetric ? (
            <div className="aggregate-container-multi">
              {aggregateDataByMetric.map((aggregateObject, index) => {
                return (
                  <div key={index} className="aggregate-row">
                    <AggregateRow 
                      annotation={metricAnnotationMap[aggregateObject?.identifier]}
                      data={aggregateObject} 
                      handleMetricAnnotationChange={handleMetricAnnotationChange}
                      dateRangeIds={dateRangeIds}
                      showHeader={index === 0}
                      dateRangeIdToLabelMap={dateRangeIdToLabelMap}
                    />
                  </div>
                );
              })}
            </div>
          ) : <></>}

          {/* 
            radar/spiderweb chart
          */}
          {radarChartData ? (
            <div className="radar-chart-container">
              <RadarChart options={radarChartData}/>
            </div>
          ) : <></>}

          {/* 
            cumulative grouped bar charts
            one chart per row
          */}
          {cumulativeChartData ? (
            <div className="bar-chart-container-multi">
              {cumulativeChartData.map((chartData, index) => {
                const options = mapDataForMultiCompareBarChart({
                  series: chartData.series,
                  categories: chartData.categories,
                  title: chartData.title,
                  colors: teamColors.all,
                  isCumulative: true
                })
                return (
                  <div key={index} className="bar-chart-item-multi">
                    <BarChart 
                      options={options}
                      height={'inherit'}
                    />
                  </div>
                );
              })}
            </div>
          ) : <></>}
          
          {/* 
            grouped bar charts
            one chart per row
          */}
          {barChartDataByMetric ? (
            <div className="bar-chart-container-multi">
              {Object.keys(barChartDataByMetric).map((metricIdentifier, index) => {
                const chartData = barChartDataByMetric[metricIdentifier];
                const options = mapDataForMultiCompareBarChart({
                  series: chartData.series,
                  metricIdentifier,
                  categories: chartData.categories,
                  title: chartData.title,
                  colors: teamColors.all,
                  dateRangeIds,
                  decimal_places: chartData.decimal_places
                })
                return (
                  <div key={index} className="bar-chart-item-multi">
                    <BarChart 
                      options={options}
                      height={'inherit'}
                    />
                  </div>
                );
              })}
            </div>
          ) : <></>}
        </div>
      </div>
    </>
  );
}
